import { create } from "zustand";
import type {
  DimensionSelection, Filter, MetricSelection, SemanticQueryInput, Visualization, WidgetDefinitionInput,
} from "@cd/core";

export interface Draft {
  title: string;
  /** The data pack every field comes from; unset means the workspace's first (always, in a one-pack workspace). */
  pack?: string;
  metrics: MetricSelection[];
  dimensions: DimensionSelection[];
  filters: Filter[];
  sort: { key: string; dir: "asc" | "desc" }[];
  limit?: number;
  compareTo?: "previous_period" | "previous_year";
  /** How many periods back to compare with (default 1). */
  comparePeriods?: number;
  /** Show additive metrics as an average per day instead of a total. */
  averagePer?: "day";
  viz: Visualization;
  /** Visualization was picked by the user (don't auto-switch it). */
  vizPinned: boolean;
  /** From natural-language ask: interpretations to confirm. */
  assumptions?: string[];
}

export const emptyDraft = (): Draft => ({
  title: "Untitled widget", metrics: [], dimensions: [], filters: [], sort: [], viz: { type: "kpi", config: {} }, vizPinned: false,
});

export function draftFromDefinition(def: WidgetDefinitionInput, assumptions?: string[]): Draft {
  return {
    title: def.title,
    pack: def.query.pack,
    metrics: def.query.metrics,
    dimensions: def.query.dimensions ?? [],
    filters: (def.query.filters ?? []).map((f) => ({ ...f, values: f.values ?? [] })),
    sort: def.query.sort ?? [],
    limit: def.query.limit,
    compareTo: def.query.compareTo,
    comparePeriods: def.query.comparePeriods,
    averagePer: def.query.averagePer,
    viz: { type: def.visualization.type, config: def.visualization.config ?? {} },
    vizPinned: true,
    assumptions,
  };
}

/** A filter still being set up: no field chosen yet, or no value for an op that needs one. */
export const filterIncomplete = (f: Filter) => !f.ref || (f.op !== "is_null" && f.op !== "not_null" && !f.values.length);

/**
 * Widgets are saved WITHOUT a date range or scope: the dashboard (and viewer) supply those.
 * Filters still being set up are left out, so they don't empty or break the preview.
 */
export function draftToQuery(d: Draft): SemanticQueryInput {
  return {
    pack: d.pack,
    metrics: d.metrics,
    dimensions: d.dimensions,
    filters: d.filters.filter((f) => !filterIncomplete(f)),
    sort: d.sort,
    limit: d.limit,
    limitBy: d.limit ? pivotLimitBy(d) : undefined,
    compareTo: d.compareTo,
    comparePeriods: d.compareTo && d.comparePeriods && d.comparePeriods > 1 ? d.comparePeriods : undefined,
    averagePer: d.averagePer,
  };
}

/**
 * In a pivot, "top N" means the N rows with the biggest Total column (every column of each),
 * not the N biggest cells. Rows are every grouping but the last (see Pivot). Ranked by the
 * sorted metric, else the first; formulas have no Total column, so they keep a plain row limit.
 */
export function pivotLimitBy(d: Draft): SemanticQueryInput["limitBy"] {
  if (d.viz.type !== "pivot" || d.dimensions.length < 2) return undefined;
  const sorted = d.sort[0] && d.metrics.find((m) => m.kind === "metric" && m.ref === d.sort[0]!.key);
  const by = sorted ?? d.metrics[0];
  if (by?.kind !== "metric") return undefined;
  return {
    dimensions: d.dimensions.slice(0, -1).map((x) => (x.grain ? `${x.ref}:${x.grain}` : x.ref)),
    by: by.ref,
    dir: sorted ? d.sort[0]!.dir : "desc",
  };
}

export function draftToDefinition(d: Draft): WidgetDefinitionInput {
  return { title: d.title.trim() || "Untitled widget", query: draftToQuery(d), visualization: d.viz };
}

interface BuilderState {
  open: boolean;
  draft: Draft;
  widgetId?: string;
  /** Goes up each time the builder opens, so a late Ask answer can tell it's for an earlier opening. */
  session: number;
  /** The last Ask edit: what it changed, and the draft before it, for Undo. */
  askEdit?: { chips: string[]; before: Draft };
  openBuilder: (draft?: Draft, widgetId?: string) => void;
  close: () => void;
  update: (fn: (d: Draft) => Draft) => void;
  /** Replace the draft with an Ask edit of it. */
  applyAsk: (after: Draft, chips: string[]) => void;
  /** Back to the draft before the Ask edit, dropping the whole edit. */
  undoAsk: () => void;
}

export const useBuilder = create<BuilderState>((set) => ({
  open: false,
  draft: emptyDraft(),
  session: 0,
  openBuilder: (draft, widgetId) => set((s) => ({ open: true, draft: draft ?? emptyDraft(), widgetId, askEdit: undefined, session: s.session + 1 })),
  close: () => set({ open: false }),
  update: (fn) => set((s) => ({ draft: fn(s.draft) })),
  applyAsk: (after, chips) => set((s) => ({ draft: after, askEdit: { chips, before: s.draft } })),
  undoAsk: () => set((s) => (s.askEdit ? { draft: s.askEdit.before, askEdit: undefined } : {})),
}));

const REF = /[a-z_][a-z0-9_]*\.[a-z_][a-z0-9_]*/g;
export function metricRefsOf(metrics: MetricSelection[]): string[] {
  const out = new Set<string>();
  for (const m of metrics) {
    if (m.kind === "metric") out.add(m.ref);
    else for (const r of m.expr.match(REF) ?? []) out.add(r);
  }
  return [...out];
}

/** Whether the draft uses any field yet: from then on it's tied to its pack. */
export const usesFields = (d: Draft) => d.metrics.length > 0 || d.dimensions.length > 0 || d.filters.some((f) => f.ref);

/** The pack the draft reads: the one it names, else the workspace's first. */
export const draftPack = <P extends { name: string }>(d: Draft, packs: P[]): P => packs.find((p) => p.name === d.pack) ?? packs[0]!;
